import React, { useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Skeleton, Chip, IconButton, Paper, TablePagination } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCurrency, formatCurrency } from '../../../utils/currency';
import { extractTotalCount } from '../../../components/common/tableStyles';
import { SupplierService } from '../../../api/services/supplierService';

export default function SuppliersTab({
    refreshKey = 0,
    setSupplierModalOpen,
    handleOpenEditSupplier,
    handleViewSupplier,
    handleOpenDeleteSupplier
}) {
    const { currency } = useCurrency();
    const [suppliers, setSuppliers] = useState([]);
    const [suppliersLoading, setSuppliersLoading] = useState(false);
    const [totalSuppliers, setTotalSuppliers] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const fetchSuppliers = async () => {
        setSuppliersLoading(true);
        try {
            const data = await SupplierService.getSuppliers({ page, limit: rowsPerPage });
            const dataArray = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
            setSuppliers(dataArray);
            setTotalSuppliers(extractTotalCount(data, dataArray));
        } catch (error) {
            console.error("Failed to fetch paginated suppliers:", error);
        } finally {
            setSuppliersLoading(false);
        }
    };

    React.useEffect(() => {
        fetchSuppliers();
    }, [page, rowsPerPage, refreshKey]);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon sx={{ fontSize: 18 }} />} 
                    onClick={() => setSupplierModalOpen(true)}
                >
                    Create New Supplier
                </Button>
            </Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Supplier Name</TableCell>
                            <TableCell>Contact Person / Email</TableCell>
                            <TableCell>Balance / Active POs</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {suppliersLoading ? (
                            Array.from(new Array(5)).map((_, i) => (
                                <TableRow key={`skeleton-${i}`}>
                                    <TableCell><Skeleton width="80%" /></TableCell>
                                    <TableCell><Skeleton width="90%" /><Skeleton width="70%" /></TableCell>
                                    <TableCell><Skeleton width="60%" /><Skeleton width="40%" /></TableCell>
                                    <TableCell><Skeleton width="70%" /></TableCell>
                                    <TableCell><Skeleton width="50%" /></TableCell>
                                    <TableCell align="right"><Skeleton width={60} sx={{ display: 'inline-block' }} /></TableCell>
                                </TableRow>
                            ))
                        ) : suppliers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">No active suppliers found.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            suppliers.map((supplier) => (
                                <TableRow key={supplier.id} hover>
                                    <TableCell><b>{supplier.companyName || supplier.name}</b></TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{supplier.contactPerson || supplier.contact || 'N/A'}</Typography>
                                        <Typography variant="caption" color="text.secondary">{supplier.email || 'N/A'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="primary.main" fontWeight="bold">
                                            {formatCurrency(supplier.accountBalance || 0, currency)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {supplier.activePOCount || 0} Active POs
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={supplier.category || 'General'} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={supplier.status || 'Active'} 
                                            size="small" 
                                            color={(supplier.status || 'Active').toLowerCase() === 'active' ? 'success' : 'warning'} 
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                            <IconButton size="small" color="primary" onClick={() => handleOpenEditSupplier(supplier)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="info" onClick={() => handleViewSupplier(supplier.id)}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleOpenDeleteSupplier(supplier.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={suppliers.length === 0 ? 0 : totalSuppliers}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>
        </Box>
    );
}
